import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom'; 
import { signIn } from '../../reducers/authReducer';
import { RootState } from '../../store/store';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import logo from '../../assets/media/logo-dark.svg';     
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Checkbox, FormControlLabel } from '@mui/material';  
import { VALIDATION_ERROR_REQUIRED_MESSAGE } from '../../constants/actions';
import { SignInFormStyles } from '../../assets/css/signInForm.styles'; 
import { GeneralStyles } from '../../assets/css/general.styles';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import CustomTextField from '../../components/customTextField/customTextField';
import CustomButton from '../../components/customButton/customButton';
import Loader from '../../components/loader';

const SignInForm: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [touched, setTouched] = useState({ username: false, password: false });
  const authState = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => setUsername(event.target.value);
  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => setPassword(event.target.value);
  const handleRememberMeChange = (event: React.ChangeEvent<HTMLInputElement>) => setRememberMe(event.target.checked);
 

  useEffect(() => {
    if (authState.token) {
      navigate('/');
    } else if (authState.error) {
      toast.error(authState.message);
    }
  }, [authState.token, authState.error, navigate]); 

  const validate = () => {
    setTouched({ username: true, password: true });
    return username !== '' && password !== '';
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (validate()) { 
      dispatch(signIn({ userName: username, password, rememberMe })).unwrap()  
      .then((response) => { 
          const { message, success } = response; 
           if (!success) { 
            toast.error(`${message}`);  
          } 
        }).catch((error) => { 
              console.log("An unexpected error occurred. Please try again.");  
        });
    }
  };

  return (
    <Container component="main" maxWidth="xs" sx={SignInFormStyles.container}>
      <div style={{ ...SignInFormStyles.box }}>
        {authState.isLoading && <Loader />}
        <img src={logo} alt="Logo" style={SignInFormStyles.logo} />
        <div> 
          <Typography variant="h5" component="h2" align="center" sx={{ marginBottom:"25px" }}>
            iMowazi Crowdfunding Backoffice
          </Typography>
        </div> 
        <Box component="form" onSubmit={handleSubmit} noValidate style={SignInFormStyles.form}>
          <CustomTextField
            label="Username"
            name="username"
            type="text"
            value={username}
            onChange={handleUsernameChange}
            required={true}
            autoFocus={true}
            error={touched.username && username === ''}
            helperText={touched.username && username === '' ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}` : ''}
          />
          <CustomTextField
            label="Password"
            name="password"
            type={isVisible ? "text" :"password"}
            value={password}
            icon={isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClickOnIcon={() => setIsVisible(!isVisible)}
            onChange={handlePasswordChange}
            required={true}
            error={touched.password && password === ''}
            helperText={touched.password && password === '' ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}` : ''}
          />
          <Box display="flex" justifyContent="flex-start">
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  style={GeneralStyles.checkBoxStyle}
                  onChange={handleRememberMeChange}
                  name="rememberMe"
                />
              }
              label="Remember Me"
            />
          </Box>
          <Box display="flex" justifyContent="flex-end">
            <Link to="/ForgotPassword"> Forgot Password ?</Link> 
          </Box>
          <CustomButton style={authState.isLoading ? SignInFormStyles.disableButton : SignInFormStyles.button}>
            {authState.isLoading ? 'Signing In...' : 'Sign In'}
          </CustomButton>
        </Box>
      </div>
    </Container>
  );
};

export default SignInForm;
